import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  CustomSelect,
  TextInput,
  TextArea,
  ChipInput,
  Button,
  toast,
} from "../..";
import {
  DenmarkPublish as DenmarkPublishInterface,
  SectionEntity,
  ArticleSearchItem,
} from "@/database";
import { Option } from "@/components/Select/Select";
import { Search, Home, LogIn, LayoutGrid, Star } from "lucide-react";
import { AllHostsInfo } from "@/database/constants/model";
import DenmarkSectionTree from "./DenmarkSectionTree";
import DenmarkFavSectionTree from "./DenmarkFavSectionTree";
import DenmarkSelectedArticlesChips from "./DenmarkSelectedArticlesChips";
import DenmarkMultiPublishTargets from "./DenmarkMultiPublishTargets";
import DenmarkTaxonomySection from "./DenmarkTaxonomySection";
import DenmarkEditScheduleSection from "./DenmarkEditScheduleSection";
import PriorityPanel from "./PriorityPanel";
import AddedInSectionRow from "./AddedInSectionList";
import {
  fetchDenmarkEditSectionSelectStub,
  type DenmarkEditSectionSelectStubFn,
} from "@/database/media/denmarkEditSectionSelectStubApi";

export interface DenmarkEditContentState {
  selectedArticle: any[];
  titleForUrl: string;
  title: string;
  description: string;
  seotags: string[];
  selectedFolder: any;
  status: string;
  schedule_type: string;
  privacy: string;
}

// Re-exported from the Redux model so existing imports in PriorityPanel / PriorityPreviewModal keep working
export type { PrioritySectionEntry } from "@/store/media/media.models";

interface DenmarkEditContentProps {
  publishData: DenmarkPublishInterface;
  state: DenmarkEditContentState;
  setState: React.Dispatch<React.SetStateAction<DenmarkEditContentState>>;
  handleDenmarkchange?: (section: any, actionMeta: any) => void;
  handleHostChangeFromSelect?: (host: any) => void;
  hostOptions: any[];
  selectedHost?: string;
  currentMediaDetailsForEdit: any;
  customFetchHostSection: (params: any) => void;
  disabled?: boolean;
  hosts: any[];
  hostSubsection: any[];
  denmarkHosts: any[];
  selectedChip?: number;
  handlecancelOrDelete?: (id: number, type: string) => void;
  setActiveChip?: (chip: number) => void;
  denmarkTaxonomyList: any[];
  denmarkTaxonomyListLoading?: boolean;
  getDenmarkTaxonomyList: (params: any) => void;
  getDenmarkSectionList: (params: any) => void;
  denmarkSectionList: SectionEntity[];
  getDenmarkChildSectionList: (params: any) => void;
  selectedChipId?: number | string;
  selectedChipTitle?: string;
  openPriorityPanel?: boolean;
  showPriorityPanel?: boolean;
  setShowPriorityPanel?: (val: boolean) => void;
  fetchPriorityList?: (params: any) => Promise<any>;
  priorityReviewMode?: boolean;
  onPriorityReviewModeChange?: (val: boolean) => void;
  showPriorityPreview?: boolean;
  previewSaveStatuses?: Record<
    number,
    import("./PriorityPreviewModal").PreviewSaveStatus
  >;
  savedSectionIds?: Set<number>;
  /** When true, only allow reorder & delete in priority — no media injection */
  priorityOnlyMode?: boolean;
  hasPublishedDenmark?: boolean;
  // Redux state passed as props from DenmarkEditModal connector
  prioritySectionsList: any[];
  activePrioritySectionId: number | null;
  isFetchingSection: boolean;
  allHostsList: any;
  isSectionLoading: boolean;
  favoriteSectionList: SectionEntity[];
  isFavLoading: boolean;
  globalFavSectionList: SectionEntity[];
  addedInList: any;
  isAddedInLoading: boolean;
  // Redux dispatch callbacks passed as props from DenmarkEditModal connector
  getAddedInList: (params: any) => void;
  getFavoriteDenmarkSectionList: (params: any) => void;
  markFavorite: (params: any) => Promise<any>;
  removeFavorite: (params: any) => Promise<any>;
  upsertSectionInPriorityList: (payload: any) => void;
  setActivePrioritySectionId: (id: number) => void;
  updateSectionItemsInPriorityList: (payload: any) => void;
  removeSectionFromPriorityList: (sectionId: number) => void;
  clearPrioritySectionsList: () => void;
  setPriorityFetching: (val: boolean) => void;
  setDenmarkSectionList: (payload: any) => void;
  scrollToMultiSection?: number;
  onTaxonomyBranchConflictChange?: (hasConflict: boolean) => void;
  /** Override stub wait when selecting a new section from the tree (defaults to fetchDenmarkEditSectionSelectStub). */
  sectionTreeSelectStub?: DenmarkEditSectionSelectStubFn;
  /** When true (priority save is complete — "done" screen), the left section panel is locked. */
  allPreviewDone?: boolean;
  /** Receives a validate fn that touches all required fields and returns true when all are valid. */
  onRegisterValidate?: (fn: () => boolean) => void;
  /** Draft Denmark rows from the media form (drives child chip + parent `multi_publish_targets` sync on remove). */
  currentSelectedSection?: {
    section?: number;
    job_type?: string;
    _parentSectionId?: number;
  }[];
  searchArticles?: (params: {
    keyword: string;
    hostid: string;
    isall?: boolean;
    typeid?: number[];
    searchIntent?: string;
    pageNum?: number;
    perPage?: number;
  }) => Promise<{ data: ArticleSearchItem[] }>;
  getDenmarkEntityList?: (params: {
    hostId: number | string;
    msId: number | string;
  }) => Promise<{ data: SectionEntity[] }>;
  denmarkEntityList?: SectionEntity[];
  isDenmarkEntityListLoading?: boolean;
  singleCurrentUser?: any;
}

const DenmarkEditContent: React.FC<DenmarkEditContentProps> = ({
  publishData,
  state,
  setState,
  handleDenmarkchange,
  handleHostChangeFromSelect,
  hostOptions,
  selectedHost,
  currentMediaDetailsForEdit,
  customFetchHostSection,
  disabled,
  denmarkHosts,
  hosts,
  selectedChip,
  handlecancelOrDelete,
  setActiveChip,
  denmarkTaxonomyList,
  denmarkTaxonomyListLoading = false,
  getDenmarkTaxonomyList,
  getDenmarkSectionList,
  denmarkSectionList,
  getDenmarkChildSectionList,
  selectedChipId,
  selectedChipTitle,
  openPriorityPanel,
  showPriorityPanel: showPriorityPanelProp,
  setShowPriorityPanel: setShowPriorityPanelProp,
  fetchPriorityList,
  priorityReviewMode,
  onPriorityReviewModeChange,
  showPriorityPreview,
  previewSaveStatuses,
  savedSectionIds,
  priorityOnlyMode = false,
  hasPublishedDenmark,
  // Redux state from props
  prioritySectionsList,
  activePrioritySectionId,
  isFetchingSection,
  allHostsList,
  isSectionLoading,
  favoriteSectionList,
  isFavLoading,
  globalFavSectionList,
  addedInList,
  isAddedInLoading,
  // Redux dispatch callbacks from props
  getAddedInList,
  getFavoriteDenmarkSectionList,
  markFavorite,
  removeFavorite,
  upsertSectionInPriorityList,
  setActivePrioritySectionId,
  updateSectionItemsInPriorityList,
  removeSectionFromPriorityList,
  clearPrioritySectionsList,
  setPriorityFetching,
  setDenmarkSectionList,
  hostSubsection,
  scrollToMultiSection,
  onTaxonomyBranchConflictChange,
  sectionTreeSelectStub,
  allPreviewDone = false,
  onRegisterValidate,
  currentSelectedSection: formDraftSelectedSections = [],
  searchArticles,
  getDenmarkEntityList,
  denmarkEntityList = [],
  isDenmarkEntityListLoading = false,
  singleCurrentUser,
}) => {
  const [showPriorityPanelInternal, setShowPriorityPanelInternal] = useState(
    priorityOnlyMode || (openPriorityPanel ?? false)
  );

  // ── Required-field validation ────────────────────────────────────────────────
  const [fieldsTouched, setFieldsTouched] = useState<Record<string, boolean>>(
    {}
  );
  const touchField = (name: string) =>
    setFieldsTouched((prev) => ({ ...prev, [name]: true }));

  // Clear touched state when the active chip changes so errors don't bleed.
  useEffect(() => {
    setFieldsTouched({});
  }, [selectedChip]);

  // Ref always holds the latest display values so the validator never reads
  // stale closure state.
  const latestValuesRef = useRef({
    title: "",
    title_for_url: "",
    description: "",
    seotags: [] as string[],
  });

  // Register the validator once; the modal calls it on SAVE.
  useEffect(() => {
    if (!onRegisterValidate) return;
    onRegisterValidate(() => {
      const { title, title_for_url, description, seotags } =
        latestValuesRef.current;
      setFieldsTouched({
        title: true,
        title_for_url: true,
        description: true,
        seotags: true,
      });
      return (
        title.trim().length >= 3 &&
        title_for_url.trim() !== "" &&
        description.trim().length >= 5 &&
        seotags.length > 0
      );
    });
  }, [onRegisterValidate]);
  // ── end validation ───────────────────────────────────────────────────────────

  const showPriorityPanel =
    showPriorityPanelProp !== undefined
      ? showPriorityPanelProp
      : showPriorityPanelInternal;
  const setShowPriorityPanel = (val: boolean) => {
    setShowPriorityPanelInternal(val);
    setShowPriorityPanelProp?.(val);
  };

  const selectedHostMsId = useMemo(() => {
    const hostList: AllHostsInfo[] = Array.isArray((allHostsList as any)?.hosts)
      ? (allHostsList as any).hosts
      : Array.isArray(allHostsList)
        ? allHostsList
        : [];
    const match = hostList.find(
      (h) => String(h.hostId) === String(selectedHost)
    );
    return match?.msId ?? 0;
  }, [allHostsList, selectedHost]);

  const selectedHostName = useMemo(() => {
    const hostList: AllHostsInfo[] = Array.isArray((allHostsList as any)?.hosts)
      ? (allHostsList as any).hosts
      : Array.isArray(allHostsList)
        ? allHostsList
        : [];
    const match = hostList.find(
      (h) => String(h.hostId) === String(selectedHost)
    );
    return match?.hostName ?? "";
  }, [allHostsList, selectedHost]);

  // Fetch addedIn data once when the modal first opens
  const addedInFetchedRef = useRef(false);
  useEffect(() => {
    if (
      showPriorityPanel &&
      selectedChipId &&
      selectedHost &&
      !addedInFetchedRef.current
    ) {
      addedInFetchedRef.current = true;
      getAddedInList({
        host: Number(selectedHost),
        msId: Number(selectedChipId),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPriorityPanel, selectedChipId, selectedHost]);

  const GLOBAL_PRIORITY_PARENT_ID = selectedHostMsId;

  const GLOBAL_PRIORITY_SECTION_NAME = selectedHostName ?? "Home";

  const fetchGlobalPriority = async () => {
    if (!fetchPriorityList) return;
    setPriorityFetching(true);
    const response = await fetchPriorityList({
      host: Number(selectedHost),
      parentId: GLOBAL_PRIORITY_PARENT_ID,
      parentsParentId: 0,
    });
    if (response?.status === 200) {
      upsertSectionInPriorityList({
        sectionId: GLOBAL_PRIORITY_PARENT_ID,
        sectionName: GLOBAL_PRIORITY_SECTION_NAME,
        priorityList: response.data,
      });
    }
    setPriorityFetching(false);
  };

  // Fetch global list when panel opens or selected host changes; clean up when it closes
  useEffect(() => {
    if (!showPriorityPanel) {
      clearPrioritySectionsList();
    } else if (selectedHostMsId && priorityOnlyMode) {
      clearPrioritySectionsList();
      fetchGlobalPriority();
    }
  }, [showPriorityPanel, selectedHostMsId, priorityOnlyMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each section fetch is awaited directly and routed by its own sectionId —
  // no shared pending ref means concurrent clicks can never cross-contaminate.
  const handlePrioritySectionSelect = async (section: SectionEntity) => {
    if (allPreviewDone) return;
    if (!fetchPriorityList || section.msId == null) return;
    const sectionId = section.msId;
    const sectionName = section.name ?? "";

    const alreadyFetched = prioritySectionsList.find(
      (e) => e.sectionId === sectionId
    );
    if (alreadyFetched) {
      setActivePrioritySectionId(sectionId);
      return;
    }

    setPriorityFetching(true);
    const response = await fetchPriorityList({
      host: Number(selectedHost),
      parentId: section.msId,
      parentsParentId: section?.parents?.[0]?.parentId ?? 0,
    });
    if (response?.status === 200) {
      upsertSectionInPriorityList({
        sectionId,
        sectionName,
        priorityList: response.data,
      });
    }
    setPriorityFetching(false);
  };

  const handleItemsReorder = (
    sectionId: number,
    reorderedItems: any[],
    newlyAddedList?: any[],
    deletedList?: any[]
  ) => {
    updateSectionItemsInPriorityList({
      sectionId,
      reorderedItems,
      newlyAddedList,
      deletedList,
    });
  };

  const handleRemovePrioritySection = (sectionId: number) => {
    removeSectionFromPriorityList(sectionId);
  };

  const activePrioritySectionEntry =
    prioritySectionsList.find((e) => e.sectionId === activePrioritySectionId) ??
    null;

  // Tracks whether the section list has ever successfully loaded at least once.
  const hasEverLoadedRef = useRef(false);
  useEffect(() => {
    if (denmarkSectionList.length > 0) {
      hasEverLoadedRef.current = true;
    }
  }, [denmarkSectionList]);

  const {
    selectedArticle,
    titleForUrl,
    title,
    description,
    seotags,
    selectedFolder: _selectedFolder,
    status: _status,
    schedule_type,
  } = state;

  const currentArticleByChip =
    selectedChip != null
      ? selectedArticle.find(
          (item: any) => Number(item?.section) === Number(selectedChip)
        )
      : selectedArticle[0];
  const displayTitle =
    currentArticleByChip?.title ?? title ?? publishData?.title ?? "";
  const displayDescription =
    currentArticleByChip?.desc ??
    currentArticleByChip?.description ??
    description ??
    publishData?.desc ??
    "";
  const displayTitleForUrl =
    currentArticleByChip?.title_for_url ??
    titleForUrl ??
    publishData?.title_for_url ??
    "";
  const displaySeoTags =
    currentArticleByChip?.seotags ??
    currentArticleByChip?.tags ??
    seotags ??
    publishData?.tags ??
    [];

  // Keep ref current so the registered validator always sees latest values.
  latestValuesRef.current = {
    title: displayTitle,
    title_for_url: displayTitleForUrl,
    description: displayDescription,
    seotags: displaySeoTags,
  };

  useEffect(() => {
    if (selectedHost && selectedHostMsId) {
      setDenmarkSectionList({ data: { list: [] }, isLoading: true });
      getDenmarkSectionList({
        parentId: selectedHostMsId,
        parentsParentId: 0,
        hostId: Number(selectedHost),
        startIndex: 0,
        recordsPerPage: 500,
        listActivationStatus: [0, 1, 2, 3, 4, 5, 6, 7, 9],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHost, selectedHostMsId]);

  const sectionOptionsFromHostSubsection = useMemo(
    () =>
      (hostSubsection as any[]).map((section: any) => ({
        value: section.id,
        label: `${section.id} | ${section.name} | ${section.activationStatus}`,
        title: section.name,
        path: `${section.id} | ${section.path}`,
        status: section.activationStatus,
      })),
    [hostSubsection]
  );

  const hostOptionRenderer = (option: any) => (
    <div className="d-flex align-items-center" style={{ maxWidth: "180px" }}>
      <div style={option.logoStyle} />
      <span className="ms-2 text-truncate" style={{ maxWidth: "140px" }}>
        {option.label}
      </span>
    </div>
  );

  const hostValueRenderer = (option: any) => {
    return (
      <div className="d-flex align-items-center justify-content-center">
        <div style={option.logoStyle} />
      </div>
    );
  };

  const customOptionRenderer = (option: any) => (
    <div className="d-flex flex-column gap-2 ">
      <div>{option.title}</div>
      <div className="text-muted small">{option.path}</div>
      <div className="fs-6">{option.status}</div>
    </div>
  );

  const editSectionSelectSeqRef = useRef(0);
  const [isEditSectionSelectPending, setIsEditSectionSelectPending] =
    useState(false);

  const cancelEditSectionSelectInFlight = () => {
    editSectionSelectSeqRef.current += 1;
    setIsEditSectionSelectPending(false);
  };

  useEffect(() => {
    return () => {
      editSectionSelectSeqRef.current += 1;
      setIsEditSectionSelectPending(false);
    };
  }, [showPriorityPanel, priorityOnlyMode]);

  const handleSectionTreeClick = async (section: SectionEntity) => {
    if (allPreviewDone) return;
    const mediaDefaultTitle =
      publishData?.title ??
      currentMediaDetailsForEdit?.title ??
      currentMediaDetailsForEdit?.mediaTitle ??
      "";

    const mediaDefaultTitleForUrl =
      publishData?.title_for_url ??
      currentMediaDetailsForEdit?.title_url ??
      currentMediaDetailsForEdit?.seoPath ??
      currentMediaDetailsForEdit?.seopath ??
      "";

    const option = {
      value: section.msId,
      label: `${section.msId} | ${section.name ?? ""}`,
      channel_name: section.name ?? "",
      title: mediaDefaultTitle,
      title_for_url: mediaDefaultTitleForUrl,
      path: section.path
        ? `${section.msId} | ${section.path}`
        : `${section.msId}`,
      status: section.parents?.[0]?.activationStatus ?? "",
    };
    const treeSelectMeta = {
      action: "update-option",
      source: "section-tree",
    } as const;

    const alreadyInList = selectedArticle.some(
      (item: any) => Number(item?.section) === Number(section.msId)
    );

    if (alreadyInList) {
      cancelEditSectionSelectInFlight();
      handleDenmarkchange?.(option, treeSelectMeta);
      setActiveChip?.(section.msId);
      return;
    }

    const isDenmarkPublishEditMode = !showPriorityPanel && !priorityOnlyMode;
    if (!isDenmarkPublishEditMode) {
      if (section.msId == null) return;
      cancelEditSectionSelectInFlight();
      handleDenmarkchange?.(option, treeSelectMeta);
      setActiveChip?.(section.msId);
      return;
    }

    if (section.msId == null) return;

    const seq = ++editSectionSelectSeqRef.current;
    setIsEditSectionSelectPending(true);
    const runStub = sectionTreeSelectStub ?? fetchDenmarkEditSectionSelectStub;
    try {
      await runStub(section.msId);
      if (seq !== editSectionSelectSeqRef.current) return;
      handleDenmarkchange?.(option, treeSelectMeta);
      setActiveChip?.(section.msId);
    } finally {
      if (seq === editSectionSelectSeqRef.current) {
        setIsEditSectionSelectPending(false);
      }
    }
  };

  const updateSelectedArticleByChip = (updates: Record<string, any>) => {
    if (selectedChip == null) return;

    const chip = Number(selectedChip);
    const existing = selectedArticle.find(
      (item: any) => Number(item?.section) === chip
    );
    const fromPublishData =
      publishData &&
      Number((publishData as { section?: number }).section) === chip
        ? publishData
        : null;
    const base = existing ?? fromPublishData;
    const updated = base ? { ...base, ...updates } : null;

    setState((prev) => ({
      ...prev,
      selectedArticle: prev.selectedArticle.map((item: any) =>
        Number(item?.section) === chip ? { ...item, ...updates } : item
      ),
    }));

    if (updated) {
      handleDenmarkchange?.(updated, {
        action: "update-option",
        source: "denmark-edit-content",
      });
    }
  };

  // Tracks the last chip id that had a matching article entry. Updated
  // synchronously during render so we can show a spinner on the frames where
  // selectedChip has changed but selectedArticle hasn't caught up yet.
  const lastValidChipRef = useRef<number | null>(null);

  const [sectionTab, setSectionTab] = useState<"ALL" | "FAV" | "ADDED_IN">(
    "ALL"
  );

  const hasAddedInData =
    addedInList &&
    addedInList.priorityChildLists &&
    addedInList.priorityChildLists.length > 0;

  const handleFavTabClick = () => {
    setSectionTab("FAV");
    setEntitySearchMode(false);
    getFavoriteDenmarkSectionList({
      host: Number(selectedHost),
    });
  };

  const handleAddedInTabClick = () => {
    setSectionTab("ADDED_IN");
    setEntitySearchMode(false);
  };

  // Re-fetch favorites when host changes while FAV tab is active
  useEffect(() => {
    if (sectionTab === "FAV" && selectedHost) {
      getFavoriteDenmarkSectionList({ host: Number(selectedHost) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHost]);

  const [sectionSearch, setSectionSearch] = useState("");
  const [articleSearchResults, setArticleSearchResults] = useState<
    ArticleSearchItem[]
  >([]);
  const [articleSearchLoading, setArticleSearchLoading] = useState(false);
  const [entitySearchMode, setEntitySearchMode] = useState(false);
  const [entitySearchTitle, setEntitySearchTitle] = useState("");

  const handleArticleSearchCallback = async (params: {
    keyword: string;
    hostid?: string;
  }) => {
    if (!params.keyword || !searchArticles) return;
    setArticleSearchLoading(true);
    const response = await searchArticles({
      keyword: params.keyword,
      hostid: params.hostid ?? selectedHost ?? "",
      isall: true,
      typeid: [2, 3, 23, 18, 19, 9],
      searchIntent: "autocomplete",
      pageNum: 0,
      perPage: 50,
    });
    setArticleSearchLoading(false);
    if (response?.data) {
      setArticleSearchResults(response.data);
    }
  };

  const articleSearchOptions = useMemo<Option[]>(
    () =>
      articleSearchResults.map((item) => {
        const isFav = globalFavSectionList.some((s) => s.msId === item.msid);
        return {
          label: item.title,
          value: item.msid,
          customLabel: (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
                width: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    fontWeight: 500,
                    fontSize: "13px",
                    color: "#1a1a1a",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.title}
                </span>
                <span
                  style={{
                    fontSize: "11px",
                    color: "#6c757d",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.path}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontSize: "11px",
                    color: "#6c757d",
                    background: "#f0f0f0",
                    borderRadius: "4px",
                    padding: "2px 6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.msid}
                </span>
                <span
                  onClick={async (e) => {
                    e.stopPropagation();
                    const params = {
                      msId: item.msid,
                      name: item.title,
                      path: item.path,
                      parents: [
                        {
                          parentId: item.parentid,
                          hostId: item.hostid[0],
                        },
                      ],
                      activationStatus: "ACTIVE",
                      entityType: "SECTION",
                      type: "1-0",
                    };
                    if (isFav) {
                      const res = await removeFavorite(params);
                      if (res?.status === 200) {
                        toast.success("Removed from favorites");
                      } else {
                        toast.error(
                          res?.error ||
                            "Could not remove favorite, please try again"
                        );
                      }
                    } else {
                      const res = await markFavorite(params);
                      if (res?.status === 200) {
                        toast.success("Marked as favorite");
                      } else {
                        toast.error(
                          res?.error ||
                            "Could not mark as favorite, please try again"
                        );
                      }
                    }
                  }}
                  style={{ cursor: "pointer", lineHeight: 1, flexShrink: 0 }}
                >
                  <Star
                    size={14}
                    fill={isFav ? "#f5a623" : "none"}
                    stroke={isFav ? "#f5a623" : "#6c757d"}
                  />
                </span>
              </div>
            </div>
          ),
        };
      }),
    [articleSearchResults, globalFavSectionList, markFavorite, removeFavorite]
  );

  const activeSectionList =
    sectionTab === "FAV" ? favoriteSectionList : denmarkSectionList;

  const filteredSectionList = useMemo(() => {
    if (!sectionSearch.trim()) return activeSectionList;
    const query = sectionSearch.toLowerCase();
    const filterTree = (sections: SectionEntity[]): SectionEntity[] =>
      sections.reduce<SectionEntity[]>((acc, section) => {
        const nameMatch = (section.name ?? "").toLowerCase().includes(query);
        // If this node matches, keep its subtree as-is so lazy-loaded subsections
        // still appear (their names may not include the search query).
        const filteredChildren = nameMatch
          ? [...(section.childSections ?? [])]
          : filterTree(section.childSections ?? []);
        if (nameMatch || filteredChildren.length > 0) {
          acc.push({ ...section, childSections: filteredChildren });
        }
        return acc;
      }, []);
    return filterTree(activeSectionList);
  }, [sectionSearch, activeSectionList]);

  if (
    isSectionLoading &&
    denmarkSectionList.length === 0 &&
    !hasEverLoadedRef.current
  ) {
    return (
      <div
        className="d-flex align-items-center justify-content-center h-100 w-100"
        style={{ minHeight: "300px" }}
      >
        <div className="d-flex flex-column align-items-center gap-3">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "2.5rem", height: "2.5rem", borderWidth: "3px" }}
          >
            <span className="visually-hidden">Loading…</span>
          </div>
          <span style={{ fontSize: "0.9rem", color: "#6c757d" }}>
            Loading sections…
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex h-100" style={{ gap: "20px" }}>
      {/* Left Sidebar - Search Section: visible in priority mode OR when no denmark exists yet */}
      {(showPriorityPanel || priorityOnlyMode || !hasPublishedDenmark) && (
        <div
          className="border-end pe-3"
          style={{ width: "280px", minWidth: "280px" }}
        >
          {/* Search Input + Host Dropdown + Home Button */}
          <div className="d-flex align-items-center gap-1 mb-3">
            <div
              className="demarkCont position-relative flex-grow-1"
              style={{ minWidth: 0 }}
            >
              <Search
                className="searchInput"
                size={18}
                style={{ top: "11px" }}
              />
              <CustomSelect
                className="w-100"
                name="sectionArticleSearch"
                label=""
                value={[]}
                placeholder="Search sections..."
                options={[]}
                updatedOptions={articleSearchOptions}
                onInputChangeCallback={handleArticleSearchCallback}
                callbackParams={selectedHost}
                customOptionRenderer={(option) =>
                  option.customLabel as React.ReactNode
                }
                hideArrow={true}
                hostSubSection={true}
                loader={articleSearchLoading}
                customStyling={{
                  padding: "0 48px 0 28px",
                }}
                handleChange={(selected) => {
                  if (selected && !Array.isArray(selected)) {
                    const opt = selected as Option;
                    setEntitySearchTitle(String(opt.label));
                    setEntitySearchMode(true);
                    setSectionTab("ALL");
                    getDenmarkEntityList?.({
                      hostId: selectedHost ?? "",
                      msId: opt.value as number,
                    });
                  }
                }}
                noOptionsMessage={() => "No articles found"}
              />
              <div className="position-absolute end-0 top-0 bottom-0">
                <CustomSelect
                  className="cursor-pointer h-100"
                  name="selectedHost"
                  value={(() => {
                    const matched = hostOptions?.find(
                      (option: any) =>
                        String(option.value) === String(selectedHost)
                    );
                    return matched
                      ? matched.value
                      : hostOptions?.[0]?.value || "";
                  })()}
                  handleChange={handleHostChangeFromSelect}
                  options={hosts && hosts.length > 0 ? hostOptions : []}
                  ishost={true}
                  hideArrow={true}
                  disabled={
                    !hostOptions?.length ||
                    (showPriorityPanel && !priorityOnlyMode) ||
                    selectedArticle?.length > 0
                  }
                  customOptionRenderer={hostOptionRenderer}
                  customValueRenderer={hostValueRenderer}
                  lowOpacity={false}
                  menuWidth="200px"
                  menuAlign="extend-right"
                  isMenuWidthFixed={true}
                />
              </div>
            </div>
            {showPriorityPanel && !priorityOnlyMode && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center justify-content-center flex-shrink-0 align-self-stretch p-0 border-0 bg-transparent"
                style={{ width: "38px" }}
                title="Add Section"
                onClick={() => {
                  const alreadyExists = prioritySectionsList.some(
                    (e) => e.sectionId === GLOBAL_PRIORITY_PARENT_ID
                  );
                  if (!alreadyExists) {
                    fetchGlobalPriority();
                  } else {
                    setActivePrioritySectionId(GLOBAL_PRIORITY_PARENT_ID);
                  }
                }}
              >
                <span
                  style={{
                    fontSize: "22px",
                    fontWeight: "700",
                    color: "#000",
                    lineHeight: 1,
                  }}
                >
                  +
                </span>
              </button>
            )}
            {showPriorityPanel && priorityOnlyMode && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center flex-shrink-0 align-self-stretch p-0 rounded"
                style={{ width: "38px" }}
                title="Global Priority List"
                onClick={() => {
                  const alreadyExists = prioritySectionsList.some(
                    (e) => e.sectionId === GLOBAL_PRIORITY_PARENT_ID
                  );
                  if (!alreadyExists) {
                    fetchGlobalPriority();
                  } else {
                    setActivePrioritySectionId(GLOBAL_PRIORITY_PARENT_ID);
                  }
                }}
              >
                <Home size={16} />
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="d-flex gap-2 mb-3">
            {hasAddedInData && (
              <Button
                type="outline-secondary"
                className="btn-sm"
                style={
                  sectionTab === "ADDED_IN"
                    ? {
                        flex: "1 1 0",
                        backgroundColor: "#e3f2fd",
                        color: "#1976d2",
                        border: "none",
                        fontWeight: "500",
                      }
                    : { flex: "1 1 0", fontWeight: "500" }
                }
                // icon={<LogIn size={14} />}
                onClick={handleAddedInTabClick}
                HTMLButtonType="button"
              >
                ADDED IN
              </Button>
            )}
            <Button
              type="outline-secondary"
              className="btn-sm"
              style={
                sectionTab === "ALL"
                  ? {
                      flex: "1 1 0",
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      border: "none",
                      fontWeight: "500",
                    }
                  : { flex: "1 1 0", fontWeight: "500" }
              }
              icon={<LayoutGrid size={14} />}
              onClick={() => {
                setSectionTab("ALL");
                setEntitySearchMode(false);
              }}
              HTMLButtonType="button"
            >
              ALL
            </Button>
            <Button
              type="outline-secondary"
              className="btn-sm"
              style={
                sectionTab === "FAV"
                  ? {
                      flex: "1 1 0",
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      border: "none",
                      fontWeight: "500",
                    }
                  : { flex: "1 1 0", fontWeight: "500" }
              }
              icon={<Star size={14} />}
              onClick={handleFavTabClick}
              HTMLButtonType="button"
            >
              FAV
            </Button>
          </div>

          <div className="mb-3">
            {entitySearchMode ? (
              <>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 px-2 py-1"
                    style={{ fontSize: "12px" }}
                    onClick={() => setEntitySearchMode(false)}
                  >
                    ← Back
                  </button>
                  <span
                    className="text-truncate"
                    style={{ fontSize: "12px", fontWeight: 500, color: "#333" }}
                    title={entitySearchTitle}
                  >
                    {entitySearchTitle}
                  </span>
                </div>
                {isDenmarkEntityListLoading ? (
                  <div
                    className="d-flex flex-column align-items-center justify-content-center gap-2"
                    style={{ minHeight: "120px" }}
                  >
                    <div
                      className="spinner-border text-primary"
                      role="status"
                      style={{
                        width: "1.8rem",
                        height: "1.8rem",
                        borderWidth: "2px",
                      }}
                    >
                      <span className="visually-hidden">Loading…</span>
                    </div>
                  </div>
                ) : (
                  <DenmarkSectionTree
                    sections={denmarkEntityList}
                    selectedSectionIds={selectedArticle.map(
                      (item: any) => item.section
                    )}
                    onSectionClick={handleSectionTreeClick}
                    getDenmarkChildSectionList={getDenmarkChildSectionList}
                    isSectionLoading={isSectionLoading}
                    showPriorityPanel={showPriorityPanel}
                    onPrioritySectionSelect={handlePrioritySectionSelect}
                    selectedHost={selectedHost}
                    disablePlayButton={allPreviewDone}
                  />
                )}
              </>
            ) : sectionTab === "ADDED_IN" ? (
              isAddedInLoading ? (
                <div
                  className="d-flex flex-column align-items-center justify-content-center gap-2"
                  style={{ minHeight: "120px" }}
                >
                  <div
                    className="spinner-border text-primary"
                    role="status"
                    style={{
                      width: "1.8rem",
                      height: "1.8rem",
                      borderWidth: "2px",
                    }}
                  >
                    <span className="visually-hidden">Loading…</span>
                  </div>
                  <span className="text-muted small">
                    Loading added-in sections…
                  </span>
                </div>
              ) : (
                <div
                  className=""
                  style={{ maxHeight: "500px", overflowY: "auto" }}
                >
                  {(() => {
                    const map = addedInList?.msidToTitlePathMap ?? {};
                    const contentMsid = String(addedInList?.contentMsid ?? "");
                    const mainaddedIds = addedInList?.priorityChildLists?.map(
                      (c: any) => String(c.masMsid)
                    );
                    const sectionEntries = Object.entries(map).filter(([key]) =>
                      mainaddedIds.includes(key)
                    );
                    if (sectionEntries.length === 0) {
                      return (
                        <div className="text-muted small px-2 py-1">
                          No added-in sections found.
                        </div>
                      );
                    }
                    return sectionEntries.map(([sectionMsId, pathArr]) => {
                      const title = (pathArr as any[])?.[0] ?? "Untitled";
                      const fullPath = (pathArr as any[])?.[1] ?? "";
                      const childEntry = addedInList?.priorityChildLists?.find(
                        (c: any) => String(c.masMsid) === sectionMsId
                      );
                      return (
                        <AddedInSectionRow
                          key={sectionMsId}
                          sectionMsId={Number(sectionMsId)}
                          title={title}
                          fullPath={fullPath}
                          hostId={childEntry?.hostid ?? Number(selectedHost)}
                          showPriorityPanel={showPriorityPanel}
                          onPrioritySectionSelect={handlePrioritySectionSelect}
                          selectedHost={selectedHost}
                          onSectionClick={handleSectionTreeClick}
                          disablePlayButton={allPreviewDone}
                        />
                      );
                    });
                  })()}
                </div>
              )
            ) : (sectionTab === "FAV" && isFavLoading) ||
              (sectionTab === "ALL" &&
                isSectionLoading &&
                denmarkSectionList.length === 0) ? (
              <div
                className="d-flex flex-column align-items-center justify-content-center gap-2"
                style={{ minHeight: "120px" }}
              >
                <div
                  className="spinner-border text-primary"
                  role="status"
                  style={{
                    width: "1.8rem",
                    height: "1.8rem",
                    borderWidth: "2px",
                  }}
                >
                  <span className="visually-hidden">Loading…</span>
                </div>
                <span className="text-muted small">
                  {sectionTab === "FAV"
                    ? "Loading favorites…"
                    : "Loading sections…"}
                </span>
              </div>
            ) : sectionTab === "FAV" ? (
              <DenmarkFavSectionTree
                sections={filteredSectionList}
                selectedSectionIds={selectedArticle.map(
                  (item: any) => item.section
                )}
                onSectionClick={handleSectionTreeClick}
                getDenmarkChildSectionList={getDenmarkChildSectionList}
                isSectionLoading={isSectionLoading}
                showPriorityPanel={showPriorityPanel}
                onPrioritySectionSelect={handlePrioritySectionSelect}
                selectedHost={selectedHost}
                disablePlayButton={allPreviewDone}
              />
            ) : (
              <DenmarkSectionTree
                sections={filteredSectionList}
                selectedSectionIds={selectedArticle.map(
                  (item: any) => item.section
                )}
                onSectionClick={handleSectionTreeClick}
                getDenmarkChildSectionList={getDenmarkChildSectionList}
                isSectionLoading={isSectionLoading}
                showPriorityPanel={showPriorityPanel}
                onPrioritySectionSelect={handlePrioritySectionSelect}
                selectedHost={selectedHost}
                disablePlayButton={allPreviewDone}
              />
            )}
          </div>
        </div>
      )}

      {/* Right Content Area */}

      <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
        {showPriorityPanel ? (
          <div className="flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
            <PriorityPanel
              selectedChipId={
                selectedChipId ??
                publishData?.published_id ??
                publishData?.section ??
                ""
              }
              selectedChipTitle={
                selectedChipTitle ?? publishData?.channel_name ?? ""
              }
              denmarkPublishData={publishData}
              hostId={selectedHost}
              onClose={() => {
                if (!priorityOnlyMode) setShowPriorityPanel(false);
              }}
              prioritySectionsList={prioritySectionsList}
              activePrioritySectionId={activePrioritySectionId}
              activePrioritySectionEntry={activePrioritySectionEntry}
              onSectionChipClick={(id) => setActivePrioritySectionId(id)}
              onRemoveSectionChip={handleRemovePrioritySection}
              onItemsReorder={handleItemsReorder}
              reviewMode={priorityReviewMode ?? false}
              onReviewModeChange={onPriorityReviewModeChange ?? (() => {})}
              showPreview={showPriorityPreview ?? false}
              previewSaveStatuses={previewSaveStatuses}
              savedSectionIds={savedSectionIds}
              isFetchingPriority={isFetchingSection}
              priorityOnlyMode={priorityOnlyMode}
              singleCurrentUser={singleCurrentUser}
            />
          </div>
        ) : (
          <div
            className="flex-grow-1 overflow-auto pe-2"
            style={{ minWidth: 0 }}
          >
            <DenmarkSelectedArticlesChips
              selectedArticle={selectedArticle}
              selectedChip={selectedChip}
              hosts={hosts}
              hostOptions={hostOptions}
              denmarkHosts={denmarkHosts}
              savedSocials={currentMediaDetailsForEdit?.socials}
              onSelectChip={(sectionId: number) => {
                cancelEditSectionSelectInFlight();
                setActiveChip?.(sectionId);
              }}
              onRemoveChip={(item: any, index: number) => {
                handlecancelOrDelete?.(item.section, "cancel");
                setState((prev) => ({
                  ...prev,
                  selectedArticle: prev.selectedArticle.filter(
                    (_, i) => i !== index
                  ),
                }));
              }}
            />

            {(() => {
              if (isEditSectionSelectPending) {
                return (
                  <div
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{ minHeight: "300px", marginTop: "100px" }}
                  >
                    <div
                      className="spinner-border text-primary"
                      role="status"
                      style={{
                        width: "2rem",
                        height: "2rem",
                        borderWidth: "3px",
                      }}
                    >
                      <span className="visually-hidden">Loading…</span>
                    </div>
                  </div>
                );
              }

              const activeChipData =
                selectedChip != null
                  ? selectedArticle.find(
                      (item: any) =>
                        Number(item?.section) === Number(selectedChip)
                    )
                  : null;

              // Keep the ref current whenever we have a valid match.
              if (activeChipData) {
                lastValidChipRef.current = selectedChip ?? null;
              }

              if (!activeChipData) {
                const isTransitioning =
                  selectedChip != null &&
                  selectedChip !== lastValidChipRef.current;
                if (isTransitioning) {
                  return (
                    <div
                      className="d-flex flex-column align-items-center justify-content-center"
                      style={{ minHeight: "300px", marginTop: "100px" }}
                    >
                      <div
                        className="spinner-border text-primary"
                        role="status"
                        style={{
                          width: "2rem",
                          height: "2rem",
                          borderWidth: "3px",
                        }}
                      >
                        <span className="visually-hidden">Loading…</span>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    className="d-flex flex-column align-items-center justify-content-center text-muted"
                    style={{ minHeight: "300px", marginTop: "100px" }}
                  >
                    <i className="bi bi-arrow-left-circle mb-3 fs-1 opacity-50"></i>
                    <h5
                      className="fw-medium text-center"
                      style={{ opacity: 0.7 }}
                    >
                      Choose Section from left to start publishing
                    </h5>
                  </div>
                );
              }

              const isParentChip = true;
              const isPublishedNonParent = false;
              const isShowMultipublishTargets =
                !currentArticleByChip?.parent_id;

              return (
                <>
                  {/* SEO Details Section */}
                  <div className="mb-4">
                    {!isShowMultipublishTargets && (
                      <div className="mb-3">
                        <TextInput
                          label="Title"
                          name="title"
                          disabled={false}
                          value={displayTitle}
                          onChange={(e) => {
                            touchField("title");
                            updateSelectedArticleByChip({
                              title: e.target.value,
                            });
                          }}
                          onBlur={() => touchField("title")}
                          placeholder="Enter title"
                          showRequired={false}
                          touched={fieldsTouched["title"]}
                          error={
                            displayTitle.trim().length === 0
                              ? "Title is required"
                              : displayTitle.trim().length < 3
                                ? "Title must be at least 3 characters"
                                : undefined
                          }
                        />
                      </div>
                    )}
                    {/* <div className="mb-3">
                      <TextInput
                        label="Title for URL"
                        name="title_for_url"
                        disabled={true}
                        value={displayTitleForUrl}
                        onChange={(e) => {
                          touchField("title_for_url");
                          updateSelectedArticleByChip({
                            title_for_url: e.target.value,
                          });
                        }}
                        onBlur={() => touchField("title_for_url")}
                        placeholder="Enter title for URL"
                        showRequired={false}
                        touched={fieldsTouched["title_for_url"]}
                        error={
                          !displayTitleForUrl.trim()
                            ? "Title for URL is required"
                            : undefined
                        }
                      />
                    </div> */}
                    {!isShowMultipublishTargets && (
                      <div className="mb-3">
                        <TextArea
                          label="Description"
                          name="description"
                          disabled={false}
                          value={displayDescription}
                          onChange={(e) => {
                            touchField("description");
                            updateSelectedArticleByChip({
                              desc: e.target.value,
                            });
                          }}
                          onBlur={() => touchField("description")}
                          placeholder="Enter description"
                          showRequired={false}
                          touched={fieldsTouched["description"]}
                          error={
                            displayDescription.trim().length === 0
                              ? "Description is required"
                              : displayDescription.trim().length < 5
                                ? "Description must be at least 5 characters"
                                : undefined
                          }
                          rows={3}
                        />
                      </div>
                    )}
                    {/* <div className="mb-3">
                      <ChipInput
                        label="SEO Tags"
                        value={displaySeoTags}
                        disabled={true}
                        handleChipChange={(
                          chip: string | string[],
                          action: string
                        ) => {
                          touchField("seotags");
                          if (action === "add") {
                            const chipsToAdd =
                              typeof chip === "string" ? [chip] : chip;
                            updateSelectedArticleByChip({
                              seotags: [
                                ...new Set([...displaySeoTags, ...chipsToAdd]),
                              ],
                            });
                          } else if (action === "remove") {
                            const chipsToRemove =
                              typeof chip === "string" ? [chip] : chip;
                            updateSelectedArticleByChip({
                              seotags: displaySeoTags.filter(
                                (t: string) => !chipsToRemove.includes(t)
                              ),
                            });
                          } else if (action === "clear") {
                            updateSelectedArticleByChip({ seotags: [] });
                          }
                        }}
                        placeholder="Add SEO tags"
                        showRequired={false}
                        touched={fieldsTouched["seotags"]}
                        error={
                          fieldsTouched["seotags"] &&
                          displaySeoTags.length === 0
                            ? "At least one SEO tag is required"
                            : undefined
                        }
                      />
                    </div> */}
                  </div>

                  <DenmarkTaxonomySection
                    selectedTaxonomy={currentArticleByChip?.taxonomy ?? []}
                    denmarkTaxonomyList={denmarkTaxonomyList}
                    denmarkTaxonomyListLoading={denmarkTaxonomyListLoading}
                    getDenmarkTaxonomyList={getDenmarkTaxonomyList}
                    selectedHost={selectedHost}
                    selectedChip={selectedChip}
                    onUpdateTaxonomy={(taxonomy) =>
                      updateSelectedArticleByChip({ taxonomy })
                    }
                    onBranchConflictChange={onTaxonomyBranchConflictChange}
                  />

                  {isShowMultipublishTargets && (
                    <DenmarkMultiPublishTargets
                      currentArticle={currentArticleByChip}
                      selectedChip={selectedChip}
                      selectedHost={selectedHost}
                      disabled={disabled}
                      hostOptions={hostOptions}
                      hosts={hosts}
                      denmarkHosts={denmarkHosts}
                      sectionOptions={sectionOptionsFromHostSubsection}
                      globalFavSectionList={globalFavSectionList}
                      customFetchHostSection={customFetchHostSection}
                      handleHostChangeFromSelect={handleHostChangeFromSelect}
                      customOptionRenderer={customOptionRenderer}
                      hostOptionRenderer={hostOptionRenderer}
                      hostValueRenderer={hostValueRenderer}
                      updateSelectedArticleByChip={updateSelectedArticleByChip}
                      scrollToSection={scrollToMultiSection}
                      selectedSections={
                        currentMediaDetailsForEdit?.socials ?? []
                      }
                      selectedArticle={selectedArticle}
                      activeChip={selectedChip ?? null}
                      currentSelectedSection={formDraftSelectedSections}
                      onSyncFormStateForRemovedTarget={(sectionId) =>
                        handlecancelOrDelete?.(sectionId, "cancel")
                      }
                    />
                  )}

                  <DenmarkEditScheduleSection
                    selectedChip={selectedChip}
                    selectedArticle={selectedArticle}
                    currentArticleByChip={currentArticleByChip}
                    fallbackScheduleType={schedule_type}
                    currentMediaDetailsForEdit={currentMediaDetailsForEdit}
                    disabled={disabled}
                    onUpdateArticleByChip={updateSelectedArticleByChip}
                  />
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default DenmarkEditContent;
